import {useEffect, useRef, useState} from 'react'
import {
  useLazyGetGroupQuery,
  useSubscribeContactMutation,
  useUnSubscribeContactMutation,
} from '../../services/cellcast'
import {
  ContactGroupMap,
  GetContactListResponse,
  GetContactParams,
} from '../../services/cellcast.v2.types'
import {ContactRequest, Tag} from '../../services/cellcast.v3.types'
import {
  useDeleteContactMutation,
  useExportContactMutation,
  useLazyGetContactCountQuery,
  useLazyGetContactsQuery,
} from '../../services/cellcast.v2'
import {
  useGetContactsMutation,
  useGetContactCountMutation,
  useUpdateSubscriptionStatusMutation,
  useDeleteContactsMutation,
} from '../../services/cellcast.v3'
import {getIsDefaultRegion} from '../pages/UserPages/accounts/Overview/OverviewSlice/UserDetailsSlice'

import {isEmpty} from '../Utils/_helpers'
import {useSelector} from 'react-redux'

export function useContact(
  invoker: string,
  loadWhenInit: boolean = true,
  staticFilter?: GetContactParams
) {
  const isDefaultRegion = useSelector(getIsDefaultRegion)

  // local state
  const [params, setParams] = useState<GetContactParams>({
    groupId: '',
    limit: 10,
  })
  const [loadedCount, setLoadedCount] = useState<number>(0)
  const [totalCount, setTotalCount] = useState<number>(0)
  const [triggerFetch, setTriggerFetch] = useState(false)
  const [triggerFetchNext, setTriggerFetchNext] = useState(true)
  const [triggerFetchGroup, setTriggerFetchGroup] = useState(false)

  // temporary update
  const [contactsData, setContactData] = useState<ContactGroupMap[]>([])
  const [contactNext, setContactNext] = useState<number | null>(null)
  const [contactPrev, setContactPrev] = useState<number | null>(null)
  const [isLoadingContact, setIsLoadingContact] = useState(false)
  const [isLoadingContactCount, setIsLoadingContactCount] = useState(false)

  // handle group
  const [fetchGroup, {isLoading: isGroupLoading, data: group}] = useLazyGetGroupQuery()

  // handle contact load
  const [
    fetchContact,
    {data: contacts, isFetching: isContactFetching, isLoading: isContactLoading},
  ] = useLazyGetContactsQuery()

  const [fetchContactRegion, {data: contactsRegion, isLoading: isContactRegionLoading}] =
    useGetContactsMutation()
  const [fetchContactCountRegion, {isLoading: isContactCountRegionLoading}] =
    useGetContactCountMutation()

  const [
    fetchContactCount,
    {isFetching: isContactCountFetching, isLoading: isContactCountLoading},
  ] = useLazyGetContactCountQuery()

  // handle subscribe unsubscribe
  const [subscribe, {isLoading: isSubscribeLoading}] = useSubscribeContactMutation()
  const [unsubscribe, {isLoading: isUnsubscribeLoading}] = useUnSubscribeContactMutation()

  const [updateSubscriptionStatus, {isLoading: isUpdateSubscriptionLoading}] =
    useUpdateSubscriptionStatusMutation()

  // delete contact
  const [deleteContact, {isLoading: isDeleteLoading}] = useDeleteContactMutation()

  const [deleteContactRegion, {isLoading: isDeleteContactRegionLoading}] =
    useDeleteContactsMutation()

  // export contact
  const [exportContact, {isLoading: isExportLoading}] = useExportContactMutation()

  // listen first load
  useEffect(() => {
    fetchGroup(null, true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // listen update group id
  useEffect(() => {
    if (!isGroupLoading && group && group.data && group.data._id) {
      setParams({
        ...params,
        groupId: group.data._id,
      })
      if (loadWhenInit) {
        setTriggerFetch(true)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group, isGroupLoading])

  const getContactData = (reqParams: GetContactParams) => {
    if (isDefaultRegion) {
      fetchContact(reqParams, false).then((res) => {
        if ('data' in res) {
          const countDocument = res.data && res.data.data.items ? res.data.data.items.length : 0
          if (triggerFetchNext) {
            setLoadedCount(loadedCount + countDocument)
          } else {
            setLoadedCount(loadedCount - countDocument)
          }
        }
      })
    } else {
      const tagsIds = reqParams.tag?.trim().split(',').filter(Boolean)
      const excludeTagsIds = reqParams.excludeTag?.trim().split(',').filter(Boolean)

      let limit = reqParams.limit

      if (typeof limit === 'string') {
        limit = parseInt(limit)
      }

      const regionParam: ContactRequest = {
        page: !isEmpty(reqParams.page) ? 1 : reqParams.page,
        limit: limit,
        group_id: reqParams.groupId,
        static_tag: reqParams.staticTag,
        static_exclude_tag: reqParams.staticExcludeTag,
        status: reqParams.status,
        search: reqParams.search,
        sort_key: reqParams.sortKey,
        sort_value: reqParams.sortValue,
      }

      if (tagsIds && tagsIds.length > 0) {
        regionParam['tag_ids'] = tagsIds
      }
      if (excludeTagsIds && excludeTagsIds.length > 0) {
        regionParam['exclude_tag_ids'] = excludeTagsIds
      }

      if (reqParams.number) {
        regionParam['number'] = reqParams.number
      }

      fetchContactRegion({
        action: 'find_contact',
        data: regionParam,
      }).then((res) => {
        if ('data' in res) {
          updateRegionContactData(res.data)
          const countDocument = res.data && res.data.data?.items ? res.data.data.items?.length : 0
          if (triggerFetchNext) {
            setLoadedCount(loadedCount + countDocument)
          } else {
            setLoadedCount(loadedCount - countDocument)
          }
        }
      })
    }
  }

  const getContactCount = (reqParams: GetContactParams) => {
    if (isDefaultRegion) {
      fetchContactCount({
        groupId: reqParams.groupId,
        excludeTag: reqParams.excludeTag,
        search: reqParams.search,
        status: reqParams.status,
        tag: reqParams.tag,
        staticTag: reqParams.staticTag,
        staticExcludeTag: reqParams.staticExcludeTag,
      }).then((res) => {
        if ('data' in res) {
          setTotalCount(res.data ? res.data.data : 0)
        }
      })
    } else {
      const tagsIds = reqParams.tag?.trim().split(',').filter(Boolean)
      const excludeTagsIds = reqParams.excludeTag?.trim().split(',').filter(Boolean)

      const regionParam: any = {
        group_id: reqParams.groupId,
        status: reqParams.status,
        search: reqParams.search,
        static_tag: reqParams.staticTag,
      }

      if (tagsIds && tagsIds.length > 0) {
        regionParam['tag_ids'] = tagsIds
      }
      if (excludeTagsIds && excludeTagsIds.length > 0) {
        regionParam['exclude_tag_ids'] = excludeTagsIds
      }

      fetchContactCountRegion({
        action: 'count_contact',
        data: regionParam,
      }).then((res) => {
        if ('data' in res) {
          setTotalCount(res.data ? res.data.data : 0)
        }
      })
    }
  }
  // listen trigger fetch contact
  useEffect(() => {
    if (triggerFetch) {
      const reqParams = buildFetchParams()
      if (totalCount === 0 && loadedCount === 0) {
        getContactCount(reqParams)
      }

      if (triggerFetchGroup) {
        fetchGroup(null, false).finally(() => getContactData(reqParams))
      } else {
        getContactData(reqParams)
      }

      setTriggerFetch(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    triggerFetch,
    triggerFetchNext,
    totalCount,
    loadedCount,
    fetchContact,
    params,
    fetchContactCount,
  ])

  const triggerFetchFn = () => {
    setLoadedCount(0)
    setTotalCount(0)
    setTriggerFetch(true)
    setTriggerFetchNext(true)
  }

  const triggerFetchNextFn = () => {
    setTriggerFetch(true)
    setTriggerFetchNext(true)
  }

  const triggerFetchPrevFn = () => {
    setTriggerFetch(true)
    setTriggerFetchNext(false)
  }

  const buildFetchParams = (): GetContactParams => {
    const fetchParams = {...params}
    if (staticFilter) {
      if (staticFilter.groupId) {
        fetchParams.groupId = staticFilter.groupId
      }

      if (staticFilter.tag) {
        if (fetchParams.tag) {
          fetchParams.tag = `${fetchParams.tag},${staticFilter.tag}`
        } else {
          fetchParams.tag = staticFilter.tag
        }
      }

      if (staticFilter.excludeTag) {
        if (fetchParams.excludeTag) {
          fetchParams.excludeTag = `${fetchParams.excludeTag},${staticFilter.excludeTag}`
        } else {
          fetchParams.excludeTag = staticFilter.excludeTag
        }
      }

      if (staticFilter.status) {
        fetchParams.status = staticFilter.status
      }

      if (staticFilter.staticTag) {
        fetchParams.staticTag = staticFilter.staticTag
      }
    }

    return fetchParams
  }

  let loadedContact: number = loadedCount
  let totalContacts: number = totalCount

  useEffect(() => {
    if (isDefaultRegion) {
      if (!isContactLoading) {
      }
      if (contacts?.data.items) {
        setContactData(contacts?.data?.items)
      }

      if (contacts?.data.next) {
        setContactNext(contacts?.data.next)
      }

      if (contacts?.data.prev) {
        setContactPrev(contacts?.data.prev)
      }
      setIsLoadingContact(isContactLoading)
      setIsLoadingContactCount(isContactCountLoading)
    }
  }, [isDefaultRegion, contacts?.data, isContactLoading, isContactCountLoading])

  function updateRegionContactData(data: GetContactListResponse) {
    if (data?.data && !isContactRegionLoading && data.data?.items) {
      const mapData = data.data?.items?.map((item) => {
        const fields = []

        const objectKeys = Object.keys(item.fields)
        const existingFields: any = item.fields
        if (existingFields !== undefined && objectKeys.length > 0) {
          for (let i = 0; i < objectKeys.length; i++) {
            const key = objectKeys[i]
            fields.push({
              name: key,
              value: existingFields[key],
            })
          }
        }

        return {
          ...item,
          fields: fields,
          tags:
            item.tags?.map((tag: Tag) => ({
              ...tag,
              isHidden: !tag.isHidden,
            })) ?? [],
        }
      }) as any

      setContactData(mapData ?? [])

      if (data?.data.next) {
        setContactNext(data?.data.next)
      }

      if (data?.data.prev) {
        setContactPrev(data?.data.prev)
      }
    }

    setIsLoadingContact(isContactRegionLoading)
    setIsLoadingContactCount(isContactCountRegionLoading)
  }
  
  return {
    loading: {
      contactLoad: isLoadingContact,
      contactFetch: isContactFetching,
      contactDelete: isDefaultRegion ? isDeleteLoading : isDeleteContactRegionLoading,
      contactCountLoading: isLoadingContactCount,
      contactCountFetching: isContactCountFetching,
      groupLoad: isGroupLoading,
      subscribe: isDefaultRegion ? isSubscribeLoading : isUpdateSubscriptionLoading,
      unsubscribe: isDefaultRegion ? isUnsubscribeLoading : isUpdateSubscriptionLoading,
      export: isExportLoading,
    },
    data: {
      contacts: contactsData,
      next: contactNext,
      prev: contactPrev,
      loadedContact: loadedContact,
      totalContacts: totalContacts,

      group,
      params,
    },
    actions: {
      subscribe: isDefaultRegion ? subscribe : updateSubscriptionStatus,
      unsubscribe: isDefaultRegion ? unsubscribe : updateSubscriptionStatus,
      setParams,
      setTriggerFetch: triggerFetchFn,
      setTriggerFetchNext: triggerFetchNextFn,
      setTriggerFetchPrev: triggerFetchPrevFn,
      setTriggerFetchGroup: setTriggerFetchGroup,
      deleteContact: isDefaultRegion ? deleteContact : deleteContactRegion,
      exportContact,
    },
  }
}
